import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiEndpoints } from "../../helpers/constants";
import { getMultipleEntriesFromDB } from "../../helpers/db";
import { checkIsValidData } from "../../helpers/commonFunction";

export const reqToGetUserDetailsFromIDB = createAsyncThunk(
  `${apiEndpoints.login}/idb`,
  async ({ data, onSuccess }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isIDBChecked = state.indexedDBReducer?.isUserDetailsIDBChecked;
      const stateUserData = state.indexedDBReducer?.userData;

      if (!data && isIDBChecked) {
        onSuccess?.(stateUserData);
        return stateUserData;
      }

      const fetchData = await getMultipleEntriesFromDB(["userData"]);
      if (!fetchData || Object.keys(fetchData)?.length === 0) {
        onSuccess?.(stateUserData);
        return stateUserData;
      }
      const { userData } = fetchData;
      const userDetails =
        Array.isArray(userData) && userData.length > 0 ? userData[0] : null;
      onSuccess?.(userDetails);
      return userDetails;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const reqToGetRestaurantDetailsFromIDB = createAsyncThunk(
  `${apiEndpoints.restaurantDetails}/idb`,
  async ({ data, onSuccess }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isIDBChecked = state.indexedDBReducer?.isRestaurantListIDBChecked;
      const stateRestaurantList = state.indexedDBReducer?.restaurantList;
      const stateGovermentMarketList =
        state.indexedDBReducer?.govermentMarketList;

      const newData = {
        restaurantList: checkIsValidData(stateRestaurantList),
        govermentMarketList: checkIsValidData(stateGovermentMarketList),
      };

      if (!data && isIDBChecked) {
        onSuccess?.(newData);
        return newData;
      }

      const fetchData = await getMultipleEntriesFromDB([
        "restaurantDetails",
        "govermentMarketDetails",
      ]);
      if (!fetchData || Object.keys(fetchData)?.length === 0) {
        onSuccess?.(newData);
        return newData;
      }
      const { restaurantDetails, govermentMarketDetails } = fetchData;

      newData.restaurantList = restaurantDetails;
      newData.govermentMarketList = govermentMarketDetails;
      onSuccess?.(newData);
      return newData;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const reqToGetRestaurantReportFromIDB = createAsyncThunk(
  `${apiEndpoints.restaurantReports}/idb`,
  async ({ data, onSuccess }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isIDBChecked = state.indexedDBReducer?.isRestaurantReportIDBChecked;
      const stateRestaurantReportList =
        state.indexedDBReducer?.restaurantReportList;
      const stateGovermentMarketReportList =
        state.indexedDBReducer?.govermentMarketReportList;

      const newData = {
        restaurantReportList: checkIsValidData(stateRestaurantReportList),
        govermentMarketReportList: checkIsValidData(
          stateGovermentMarketReportList
        ),
      };

      if (!data && isIDBChecked) {
        onSuccess?.(newData);
        return newData;
      }

      const fetchData = await getMultipleEntriesFromDB([
        "restaurantDetailsReports",
        "govermentMarketReports",
      ]);
      if (!fetchData || Object.keys(fetchData)?.length === 0) {
        onSuccess?.(newData);
        return newData;
      }
      const { restaurantDetailsReports, govermentMarketReports } = fetchData;

      newData.restaurantReportList = restaurantDetailsReports;
      newData.govermentMarketReportList = govermentMarketReports;
      onSuccess?.(newData);
      return newData;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState = {
  loader: false,
  error: "",

  // USER DETAILS IDB STATES
  userData: null,
  isUserDetailsIDBChecked: false,

  // RESTAURANT DETAILS IDB STATES
  restaurantList: [],
  govermentMarketList: [],
  isRestaurantListIDBChecked: false,

  // RESTAURANT REPORT IDB STATES
  restaurantReportList: [],
  govermentMarketReportList: [],
  isRestaurantReportIDBChecked: false,
};

// Create indexedDB slice
const indexedDBSlice = createSlice({
  name: "IDB",
  initialState,
  reducers: {
    reqToEnableAllDataIDBCheck: (state) => {
      state.isUserDetailsIDBChecked = false;
      state.isRestaurantListIDBChecked = false;
      state.isRestaurantReportIDBChecked = false;
    },

    reqToEnableRestaurantDetailsIDBCheck: (state) => {
      state.isRestaurantListIDBChecked = false;
      state.isRestaurantReportIDBChecked = false;
    },
    reqToEnableRestaurantReportIDBCheck: (state) => {
      state.isRestaurantReportIDBChecked = false;
    },
  },
  extraReducers: (builder) => {
    // GET USER DETAILS from IDB
    builder
      .addCase(reqToGetUserDetailsFromIDB.pending, (state) => {
        state.loader = true;
      })
      .addCase(reqToGetUserDetailsFromIDB.fulfilled, (state, action) => {
        state.loader = false;
        state.userData = action.payload;
        state.isUserDetailsIDBChecked = true;
      })
      .addCase(reqToGetUserDetailsFromIDB.rejected, (state, action) => {
        state.loader = false;
        state.error = action.payload;
        state.isUserDetailsIDBChecked = true;
      });

    // GET RESTAURANT DETAILS from IDB
    builder
      .addCase(reqToGetRestaurantDetailsFromIDB.pending, (state) => {
        state.loader = true;
      })
      .addCase(reqToGetRestaurantDetailsFromIDB.fulfilled, (state, action) => {
        state.loader = false;
        state.restaurantList = action.payload.restaurantList;
        state.govermentMarketList = action.payload.govermentMarketList;
        state.isRestaurantListIDBChecked = true;
      })
      .addCase(reqToGetRestaurantDetailsFromIDB.rejected, (state, action) => {
        state.loader = false;
        state.error = action.payload;
        state.isRestaurantListIDBChecked = true;
      });

    // GET RESTAURANT REPORT from IDB
    builder
      .addCase(reqToGetRestaurantReportFromIDB.pending, (state) => {
        state.loader = true;
      })
      .addCase(reqToGetRestaurantReportFromIDB.fulfilled, (state, action) => {
        state.loader = false;
        state.restaurantReportList = action.payload.restaurantReportList;
        state.govermentMarketReportList =
          action.payload.govermentMarketReportList;
        state.isRestaurantReportIDBChecked = true;
      })
      .addCase(reqToGetRestaurantReportFromIDB.rejected, (state, action) => {
        state.loader = false;
        state.error = action.payload;
        state.isRestaurantReportIDBChecked = true;
      });
  },
});

export const {
  reqToEnableAllDataIDBCheck,
  reqToEnableRestaurantDetailsIDBCheck,
} = indexedDBSlice.actions;
export default indexedDBSlice.reducer;
